import React from "react";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";

const chartColors = ["#2563eb", "#0f766e", "#d97706", "#7c3aed", "#dc2626"];

const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) {
        return null;
    }

    return (
        <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-lg">
            <p className="text-sm font-semibold text-gray-900">{label}</p>
            {payload.map((entry) => (
                <p key={entry.dataKey} className="text-xs text-gray-600">
                    {entry.name}: <span className="font-medium text-gray-900">{entry.payload?.value_format || entry.value}</span>
                </p>
            ))}
        </div>
    );
};

function DashboardCharts({ series, kpis }) {
    const categoriasData = (series?.por_categoria || []);
    // const categoriasData = (series?.por_categoria || []).slice(0, 6);
    const localesData = (series?.por_local || []);
    // const localesData = (series?.por_local || []).slice(0, 6);

    const resumenData = [
        {
            name: "Facturas",
            value: Number(kpis?.monto_facturas) || 0,
            value_format: kpis?.monto_facturas_format || "Gs. 0",
        },
        {
            name: "Canjes",
            value: Number(kpis?.monto_canjes) || 0,
            value_format: kpis?.monto_canjes_format || "Gs. 0",
        },
    ].filter((item) => item.value > 0);

    return (
        <section className="mb-6 space-y-6">
            <div>
                <h2 className="text-xl font-bold text-gray-900">Visualizaciones</h2>
                <p className="text-sm text-gray-500">Lectura rapida del rendimiento por monto dentro del periodo seleccionado.</p>
            </div>

            <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.2fr_1.2fr_0.8fr]">
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <div className="mb-4">
                        <h3 className="text-base font-semibold text-gray-900">Top locales por monto</h3>
                        <p className="text-xs text-gray-500">Comparativo de los 6 locales con mayor volumen.</p>
                    </div>

                    <div className="h-[320px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={localesData} layout="vertical" margin={{ top: 0, right: 12, left: 12, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                                <XAxis type="number" tick={{ fill: "#6b7280", fontSize: 12 }} axisLine={false} tickLine={false} />
                                <YAxis
                                    type="category"
                                    dataKey="label"
                                    width={110}
                                    tick={{ fill: "#374151", fontSize: 12 }}
                                    axisLine={false}
                                    tickLine={false}
                                />
                                <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(37, 99, 235, 0.08)" }} />
                                <Bar dataKey="value" name="Monto" radius={[0, 8, 8, 0]} fill="#2563eb" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <div className="mb-4">
                        <h3 className="text-base font-semibold text-gray-900">Top categorias por monto</h3>
                        <p className="text-xs text-gray-500">Categorias con mayor impacto economico en el periodo.</p>
                    </div>

                    <div className="h-[320px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={categoriasData} layout="vertical" margin={{ top: 0, right: 12, left: 12, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                                <XAxis type="number" tick={{ fill: "#6b7280", fontSize: 12 }} axisLine={false} tickLine={false} />
                                <YAxis
                                    type="category"
                                    dataKey="label"
                                    width={110}
                                    tick={{ fill: "#374151", fontSize: 12 }}
                                    axisLine={false}
                                    tickLine={false}
                                />
                                <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(15, 118, 110, 0.08)" }} />
                                <Bar dataKey="value" name="Monto" radius={[0, 8, 8, 0]} fill="#0f766e" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <div className="mb-4">
                        <h3 className="text-base font-semibold text-gray-900">Distribucion monetaria</h3>
                        <p className="text-xs text-gray-500">Relacion entre facturas y canjes.</p>
                    </div>

                    <div className="h-[240px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={resumenData}
                                    dataKey="value"
                                    nameKey="name"
                                    innerRadius={58}
                                    outerRadius={82}
                                    paddingAngle={4}
                                >
                                    {resumenData.map((entry, index) => (
                                        <Cell key={entry.name} fill={chartColors[index % chartColors.length]} />
                                    ))}
                                </Pie>
                                <Tooltip content={<CustomTooltip />} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>

                    <div className="space-y-2">
                        {resumenData.map((item, index) => (
                            <div key={item.name} className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2">
                                <div className="flex items-center gap-2">
                                    <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: chartColors[index % chartColors.length] }} />
                                    <span className="text-sm text-gray-700">{item.name}</span>
                                </div>
                                <span className="text-sm font-semibold text-gray-900">{item.value_format}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

export default DashboardCharts;